import { DIALOG_DATA } from '@angular/cdk/dialog';
import { Component, computed, DestroyRef, inject, linkedSignal, OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { debounceTime, finalize } from 'rxjs';

import {
  HeaderEntityInstancesResponse,
  MultipleModuleScreenValuesModel,
  MultipleTransitionInfoRequest,
  SaveMultipleTransitionScreensBody,
  SaveTransitionScreenBody,
} from '@models/api';
import { IdValueType, KeyValueRoleModel } from '@models/common';
import { Role } from '@models/module/main/roles.model';
import { DocumentActionsData, NotificationFormData, NotificationFormValue } from '@module-definition/document/common';
import { DocumentActionsComponent } from '@module-definition/document/document-actions/document-actions.component';
import {
  DocumentHeaderComponent,
  DocumentHeaderData,
} from '@module-definition/document/document-header/document-header.component';
import { DynamicFormComponent } from '@module-definition/document/dynamic-form/dynamic-form.component';
import { NotificationFormComponent } from '@module-definition/document/notification-form/notification-form.component';
import { SignatureComponent } from '@module-definition/signature/signature.component';
import { DmFieldsService, DmGridFields, EntityInstanceTransition } from '@module-instance/dynamic-module/common';
import { ModuleInstanceService } from '@module-instance/services';
import { ModuleInstanceCommonService } from '@module-instance/services/module-instance-common.service';
import { EntityTableRow } from '@shared/components/data-list/common';
import { CipoFieldTypes } from '@shared/components/fields/common';
import { DEBOUNCE_TIMES } from '@shared/consts';
import { DialogFullscreenState } from '@shared/dialogs/full-screen-dialog.base';
import { CipoDialogModule } from '@shared/modules';
import { UserService } from '@shared/services';
import { UserStore } from '@signal-store/index';

export type TransitionDialogData = {
  transition: EntityInstanceTransition;
  role: Role;
  rows: EntityTableRow[];
  workflowId: number;
  presetIds?: number[];
  info?: MultipleTransitionInfoRequest[];
  signatureIsRequired: boolean;
  startScreenValues?: IdValueType<any, number>[];
};

@Component({
  selector: 'cipo-transition-dialog',
  imports: [
    NgxSkeletonLoaderModule,
    CipoDialogModule,
    DocumentHeaderComponent,
    DocumentActionsComponent,
    NotificationFormComponent,
    SignatureComponent,
    DynamicFormComponent,
  ],
  templateUrl: './transition-dialog.component.html',
})
export class TransitionDialogComponent implements OnInit {
  data = inject<TransitionDialogData>(DIALOG_DATA);
  destroyRef = inject(DestroyRef);
  moduleInstanceCommonService = inject(ModuleInstanceCommonService);
  moduleInstanceService = inject(ModuleInstanceService);
  userStore = inject(UserStore);
  userService = inject(UserService);
  readonly dialogRef = inject(MatDialogRef<TransitionDialogComponent>);
  readonly fullscreen = new DialogFullscreenState(this.dialogRef);
  dmFieldsService = inject(DmFieldsService);
  dynamicFormComponent = viewChild<DynamicFormComponent>('dynamicForm');

  currentModule = this.userStore.menuItemsList().find(m => m.moduleId === this.data.rows[0].module_id);
  headerInfo = signal<HeaderEntityInstancesResponse>(null);
  fullDocNumbers = signal('');
  gridFields = signal<DmGridFields>(null);
  submitting = signal(false);
  formValid = signal(false);
  assignment = signal<KeyValueRoleModel<string, string>>(null);
  notificationFormValue = signal<NotificationFormValue>({});
  form = new FormGroup({});

  showNotificationForm = computed(() => {
    const { hideRolesNotify, emailsDataSource } = this.gridFields() || {};
    return hideRolesNotify === false && emailsDataSource?.length;
  });

  headerData = computed<DocumentHeaderData>(() => {
    const { entity_instance_id: entityInstanceId, module_id: moduleId, is_draft: isDraft } = this.data.rows[0];
    const { ballInCourt, state, stateColor, stateTextColor, contractName, contractNumber } = this.headerInfo() || {};
    return {
      moduleId,
      entityInstanceId,
      isDraft,
      ballInCourt,
      state,
      stateColor,
      stateTextColor,
      contractName,
      contractNumber,
    };
  });

  actionsData = computed<DocumentActionsData>(() => {
    const headerInfo = this.headerInfo();
    const gridFields = this.gridFields();
    // Do not display actions until we have both header info and grid fields
    if (!(headerInfo || this.isPreset) || !gridFields) {
      return null;
    }
    const { rows, signatureIsRequired, transition } = this.data;
    const { contract_id: contractId, module_id: moduleId } = rows[0];
    return {
      moduleId,
      contractId,
      disableActions: this.submitting(),
      assignee: headerInfo?.assignee || null,
      assignOptions: gridFields.manualAssignUsers || null,
      canAssign: gridFields.canChangeAssignment,
      disableSubmit: !this.formValid() || (signatureIsRequired && !this.userHasSignature()),
      submitLabel: transition.name,
      entityInstanceId: rows[0].entity_instance_id,
      hideAssign: !gridFields.canChangeAssignment || gridFields.hideRolesNotify || this.isPreset,
    };
  });

  notificationData = computed<NotificationFormData>(() => {
    const { emails, emailsDataSource, roleToExcludeIds, fields } = this.gridFields() || {};
    return {
      contractId: this.docProps.contractId,
      moduleId: this.docProps.moduleId,
      notify: emails ?? [],
      notifyOptions: emailsDataSource ?? [],
      excludeRoles: roleToExcludeIds ?? [],
      includeAttachments: false,
      /* For drafts - always include attachments (refers to document attachments).
      For other transitions - show includeAttachments if there is any attachment field (refers to attachment fields in the form) */
      hideIncludeAttachments: this.data.rows[0].is_draft
        ? false
        : !fields?.some(({ fieldOptions }) => fieldOptions?.type === CipoFieldTypes.Attachment),
    };
  });

  userHasSignature = linkedSignal(() => !!this.userStore.userData()?.signature);

  get docProps() {
    const isAction = this.data.transition.isComment || this.data.transition.isEmail || false;
    const {
      rows,
      transition: { id: transitionId },
      workflowId,
    } = this.data;
    const { entity_instance_id: entityInstanceId, contract_id: contractId, module_id: moduleId } = rows[0];
    return {
      entityInstanceId,
      contractId,
      moduleId,
      transitionId: isAction ? null : transitionId,
      workflowId,
      actionId: isAction ? transitionId : null,
      draftValues: this.data.startScreenValues,
    };
  }

  get isPreset() {
    return !!this.data.rows[0].entity_template_id;
  }

  ngOnInit() {
    this.listenFormChanges();
    this.loadGridFields();

    if (!this.isPreset) {
      /* Only load header info if entity_instance_id is present (meaning it's not a preset) */
      this.loadHeaderInfo();
    }
  }

  private loadHeaderInfo() {
    const { contractId, moduleId } = this.docProps;
    const ids = this.data.rows.map(r => r.entity_instance_id);
    this.moduleInstanceService.getMultipleHeaderInfo({ ids, contractId, moduleId }).subscribe(info => {
      this.fullDocNumbers.set(info.map(i => i.moduleDocumentNumber).join(', '));
      this.headerInfo.set(info[0]);
    });
  }

  private listenFormChanges() {
    this.form.valueChanges
      .pipe(debounceTime(DEBOUNCE_TIMES.short), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.formValid.set(this.form.valid));
  }

  private loadGridFields() {
    this.dmFieldsService.getGridFields(this.docProps).subscribe(fields => {
      this.gridFields.set(fields);

      if (!fields.fields?.length) {
        this.formValid.set(true); // No fields to fill, form is valid
        return;
      }

      // Auto-submit if there are no fields and no notifications to set
      if (
        !fields.fields?.length &&
        (!fields.requiresNonBindingSignature || this.userHasSignature()) &&
        (fields.hideRolesNotify || !fields.emailsDataSource?.length)
      ) {
        this.requestSubmit();
        return;
      }

      setTimeout(() => {
        /* Set formValid after controls are added */
        this.formValid.set(this.form.valid);
      }, 10);
    });
  }

  signatureSet() {
    this.userHasSignature.set(true);
  }

  onNotificationValueChanged(value: NotificationFormValue) {
    this.notificationFormValue.set(value);
  }

  onAssignmentChanged(assignment: KeyValueRoleModel<string, string>) {
    this.assignment.set(assignment);
  }

  /* This is triggered by button click. 
  It calls the submit method of the dynamic form component, and that method emits the formValuesPrepared event if there is no formula calculation in progress.
  If there is, it will emit the event after the calculation is done.
  */
  requestSubmit() {
    this.submitting.set(true);
    this.dynamicFormComponent().submit();
  }

  submit(formValue: IdValueType<any, number>[]) {
    const { moduleId, transitionId, contractId, workflowId, actionId } = this.docProps;

    this.userService.openLoadingDialog(actionId ? 'action' : 'transition');

    const asignee = this.assignment();
    let { notify, excludeRoles, includeAttachments } = this.notificationFormValue();

    if (!this.showNotificationForm()) {
      notify = this.gridFields().emails ?? [];
      excludeRoles = this.gridFields().roleToExcludeIds ?? [];
      includeAttachments = false;
    }

    const groups: string[] = [];
    const emails: string[] = [];
    notify?.forEach(n => {
      if (n.includes('@')) {
        emails.push(n);
      } else {
        groups.push(n);
      }
    });

    const body: SaveTransitionScreenBody = {
      groups,
      emails,
      values: formValue,
      includeAttachments,
      roleToExcludeIds: excludeRoles,
      manualAssignUser: asignee,
      workflowGroupId: this.gridFields()?.workflowGroups?.find(wg => wg.isDefault)?.key,
    };

    const roleId = this.data.role?.key ?? undefined;

    if (this.data.transition.isComment) {
      const commentBody: MultipleModuleScreenValuesModel = {
        entityInstanceIds: this.data.rows.map(r => r.entity_instance_id),
        values: body,
      };
      this.moduleInstanceCommonService
        .addMultipleComment(commentBody, moduleId, actionId, contractId, roleId)
        .pipe(
          finalize(() => {
            this.submitting.set(false);
            this.userService.closeLoadingDialog();
          }),
        )
        .subscribe(() => this.dialogRef.close(commentBody.entityInstanceIds));
    } else if (this.data.transition.isEmail) {
      // not implemented for now
    } else {
      const transitionBody: SaveMultipleTransitionScreensBody = {
        ...body,
        entityInstanceIds: this.data.rows.map(r => r.entity_instance_id),
        presetIds: this.data.presetIds,
        info: this.data.info,
        startScreenValues: this.data.startScreenValues,
      };
      this.moduleInstanceCommonService
        .saveMultipleTransitionScreensOrTemplates(
          { moduleId, workflowId, contractId, transitionId, roleId },
          transitionBody,
        )
        .pipe(
          finalize(() => {
            this.submitting.set(false);
            this.userService.closeLoadingDialog();
          }),
        )
        .subscribe(ids => this.dialogRef.close(ids));
    }
  }
}
